import { useCallback, useEffect, useRef, useState } from 'react';
import { GetPatientsPageAPI } from '../../../api/API';

const SEARCH_DELAY_MS = 350;

/**
 * Página da tela de pacientes, pedida ao servidor (get_patients.php com limit):
 * a busca e o status são aplicados lá, então uma conta com milhares de pessoas
 * baixa só as linhas da página em vez da lista inteira.
 * Sem os excluídos (lápide); os totais dos cartões vêm na mesma resposta.
 */
function usePatientsPage(companyId) {
  const [search, setSearchState] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatusState] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPageState] = useState(10);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({ all: 0, active: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const requestIdRef = useRef(0);

  // Digitar não dispara uma busca por tecla
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (companyId == null) return;
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    GetPatientsPageAPI(companyId, {
      page: page + 1,
      limit: rowsPerPage,
      search: debouncedSearch,
      status,
    }, (result) => {
      // Resposta de uma busca antiga não sobrescreve a atual
      if (requestId !== requestIdRef.current) return;
      setIsLoading(false);
      if (!result || !Array.isArray(result.patients)) {
        window.visualLogger?.error('Erro ao carregar pacientes: ' + (result?.message || result?.error || 'resposta inválida'));
        return;
      }
      setRows(result.patients);
      setTotal(Number(result.total) || 0);
      setTotals({ all: Number(result.totalAll) || 0, active: Number(result.activeAll) || 0 });
    });
  }, [companyId, page, rowsPerPage, debouncedSearch, status, reloadKey]);

  const setSearch = useCallback((value) => { setSearchState(value); setPage(0); }, []);
  const setStatus = useCallback((value) => { setStatusState(value); setPage(0); }, []);
  const setRowsPerPage = useCallback((value) => { setRowsPerPageState(value); setPage(0); }, []);
  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  return {
    rows, total, totals, isLoading,
    search, setSearch, status, setStatus,
    page, setPage, rowsPerPage, setRowsPerPage,
    reload,
  };
}

export default usePatientsPage;
